import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMessageSquare, FiMail, FiStar, FiSend, FiCheck } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { apiUrls, getHttpHeaders } from '../config/api';

interface FeedbackPageProps {
  onHomeClick: () => void;
  onBooksClick: () => void;
  onVideoLearningClick: () => void;
  onBlogsClick: () => void;
  onFaqClick?: () => void;
  onFeedbackClick?: () => void;
  onLoginClick: () => void;
  onAdminClick: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
  isAuthenticated?: boolean;
  isAdmin?: boolean;
  user?: any;
  onLogout?: () => void;
}

const FeedbackContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);
  padding-top: 120px;
`;

const FeedbackContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeedbackInfoSection = styled.div`
  background: linear-gradient(135deg, #253C8C 0%, #1e40af 100%);
  padding: 3rem 2.5rem;
  border-radius: 16px;
  color: white;
  box-shadow: 0 8px 30px rgba(37, 60, 140, 0.2);
`;

const FeedbackFormSection = styled.div`
  background: white;
  padding: 3rem 2.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(37, 60, 140, 0.1);
`;

const SectionTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: inherit;
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  line-height: 1.6;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  font-size: 1rem;
  
  .icon {
    margin-right: 1rem;
    font-size: 1.2rem;
    opacity: 0.8;
  }
`;

const Form = styled.form`
  .form-group {
    margin-bottom: 1.5rem;
  }
  
  .label {
    display: block;
    font-size: 1rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.5rem;
  }
  
  .required {
    color: #ef4444;
  }
  
  .input, .select, .textarea {
    width: 100%;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 16px;
    font-size: 1rem;
    background: white;
    outline: none;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: #253C8C;
      box-shadow: 0 0 0 3px rgba(37, 60, 140, 0.1);
    }
    
    &::placeholder {
      color: #9ca3af;
    }
  }
  
  .textarea {
    min-height: 120px;
    resize: vertical;
    font-family: inherit;
    line-height: 1.5;
  }
  
  .select {
    cursor: pointer;
  }
`;

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const RatingStar = styled.button<{ filled: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 24px;
  color: ${props => props.filled ? '#F9D51D' : '#e5e7eb'};
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    transform: scale(1.1);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #253C8C 0%, #1e40af 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(37, 60, 140, 0.3);
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SuccessMessage = styled.div`
  background: #dcfce7;
  border: 2px solid #22c55e;
  color: #166534;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const FeedbackPage: React.FC<FeedbackPageProps> = ({
  onHomeClick,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onFaqClick,
  onFeedbackClick,
  onLoginClick,
  onAdminClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick,
  isAuthenticated,
  isAdmin,
  user,
  onLogout
}) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    feedback_type: 'general',
    subject: '',
    message: '',
    rating: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const feedbackTypes = [
    { value: 'general', label: 'General Feedback' },
    { value: 'bug_report', label: 'Bug Report' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'course_feedback', label: 'Course Feedback' },
    { value: 'content_feedback', label: 'Content Feedback' },
    { value: 'technical_issue', label: 'Technical Issue' },
    { value: 'suggestion', label: 'Suggestion' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Get current page source
      const pageSource = window.location.search.includes('page=') 
        ? new URLSearchParams(window.location.search).get('page') 
        : 'feedback';

      const submissionData = {
        ...formData,
        page_source: pageSource,
        rating: formData.rating > 0 ? formData.rating : null
      };

      console.log('🔍 Submitting feedback to:', apiUrls.feedback.submit);
      console.log('🔍 Submission data:', submissionData);
      
      const response = await fetch(apiUrls.feedback.submit, {
        method: 'POST',
        headers: getHttpHeaders(),
        body: JSON.stringify(submissionData)
      });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response URL:', response.url);

      const data = await response.json();

      if (data.success) {
        setShowSuccessModal(true);
        setFormData({ 
          name: user?.name || '', 
          email: user?.email || '', 
          phone: '', 
          feedback_type: 'general',
          subject: '', 
          message: '',
          rating: 0
        });
      } else {
        alert(data.error || 'Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <FeedbackContainer>
        <Header 
          onHomeClick={onHomeClick} 
          onBooksClick={onBooksClick} 
          onVideoLearningClick={onVideoLearningClick} 
          onBlogsClick={onBlogsClick} 
          onLoginClick={onLoginClick} 
          onAdminClick={onAdminClick}
          onMkAcademyOnlineClick={onMkAcademyOnlineClick}
          onMkAcademyInPersonClick={onMkAcademyInPersonClick}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          user={user}
          onLogout={onLogout}
          currentPage="feedback"
        />
        
        <FeedbackContent>
          <FeedbackInfoSection>
            <SectionTitle>Share Your Feedback</SectionTitle>
            <SectionSubtitle>
              Your feedback helps us improve MathKnots and provide better learning experiences for everyone.
            </SectionSubtitle>
            
            <InfoItem>
              <FiMessageSquare className="icon" />
              <div>
                <strong>We Value Your Input</strong><br />
                Share your thoughts, suggestions, or report issues
              </div>
            </InfoItem>
            
            <InfoItem>
              <FiStar className="icon" />
              <div>
                <strong>Rate Your Experience</strong><br />
                Help us understand how we're doing
              </div>
            </InfoItem>
            
            <InfoItem>
              <FiMail className="icon" />
              <div>
                <strong>Quick Response</strong><br />
                We'll review and respond to your feedback promptly
              </div>
            </InfoItem>

            <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '8px' }}>
              <h3 style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>Feedback Types:</h3>
              <ul style={{ fontSize: '0.9rem', lineHeight: '1.6', paddingLeft: '1rem' }}>
                <li><strong>General:</strong> Overall platform feedback</li>
                <li><strong>Bug Report:</strong> Technical issues or errors</li>
                <li><strong>Feature Request:</strong> New functionality suggestions</li>
                <li><strong>Course Feedback:</strong> Comments about our courses</li>
                <li><strong>Content Feedback:</strong> Feedback about books, videos, materials</li>
              </ul>
            </div>
          </FeedbackInfoSection>

          <FeedbackFormSection>
            <SectionTitle style={{ color: '#253C8C' }}>Submit Feedback</SectionTitle>
            <SectionSubtitle style={{ color: '#718096' }}>
              Please fill out the form below and we'll get back to you soon.
            </SectionSubtitle>

            {showSuccessMessage && (
              <SuccessMessage>
                <FiCheck />
                Thank you for your feedback! We appreciate your input and will review it shortly.
              </SuccessMessage>
            )}

            <Form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="label">
                  Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="Your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label className="label">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="your.email@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label className="label">Phone (Optional)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="Your phone number"
                />
              </div>

              <div className="form-group">
                <label className="label">
                  Feedback Type <span className="required">*</span>
                </label>
                <select
                  name="feedback_type"
                  value={formData.feedback_type}
                  onChange={handleInputChange}
                  className="select"
                  required
                >
                  {feedbackTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="label">
                  Subject <span className="required">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="input"
                  placeholder="Brief description of your feedback"
                  required
                  maxLength={500}
                />
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {formData.subject.length}/500 characters
                </div>
              </div>

              <div className="form-group">
                <label className="label">
                  Message <span className="required">*</span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="textarea"
                  placeholder="Please provide detailed feedback..."
                  required
                  rows={6}
                />
              </div>

              <div className="form-group">
                <label className="label">Rate Your Experience (Optional)</label>
                <RatingContainer>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <RatingStar
                      key={star}
                      type="button"
                      filled={star <= formData.rating}
                      onClick={() => handleRatingChange(star)}
                    >
                      <FiStar />
                    </RatingStar>
                  ))}
                  {formData.rating > 0 && (
                    <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '14px' }}>
                      {formData.rating} out of 5 stars
                    </span>
                  )}
                </RatingContainer>
              </div>

              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div style={{ 
                      width: '16px', 
                      height: '16px', 
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FiSend />
                    Submit Feedback
                  </>
                )}
              </SubmitButton>
            </Form>
          </FeedbackFormSection>
        </FeedbackContent>
      </FeedbackContainer>
      
      <Footer 
        onBooksClick={onBooksClick}
        onVideoLearningClick={onVideoLearningClick}
        onBlogsClick={onBlogsClick}
        onFaqClick={onFaqClick}
        onFeedbackClick={onFeedbackClick}
        onMkAcademyOnlineClick={onMkAcademyOnlineClick}
      />

      {/* Success Modal */}
      {showSuccessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            maxWidth: '500px',
            width: '90%'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: '#22c55e',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}>
              <FiCheck size={40} color="white" />
            </div>
            
            <h2 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#253C8C',
              marginBottom: '16px'
            }}>
              Feedback Submitted Successfully!
            </h2>
            
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              marginBottom: '32px',
              lineHeight: '1.5'
            }}>
              Thank you for taking the time to share your feedback. We appreciate your input and will review it carefully to improve our platform.
            </p>
            
            <button
              onClick={() => {
                setShowSuccessModal(false);
                onHomeClick();
              }}
              style={{
                backgroundColor: '#253C8C',
                color: 'white',
                border: 'none',
                padding: '16px 32px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#1e3a8a';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#253C8C';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default FeedbackPage;

